import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ImageUploader } from '@/components/menu/ImageUploader';
import { Palette } from 'lucide-react';
import { useBrandStore } from '@/store/brandStore';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/store/authStore';
import {
  fetchBrandSettings,
  updateBrandSettings,
  type BrandSettingsForm,
} from '@/services/settings';
import { Switch } from '@/components/ui/switch';
import { isSuperAdmin } from '@/utils/roleUtils';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

export function SuperAdminBrandSettings() {
  const { t } = useTranslation();
  const {
    name,
    logo,
    colors,
    setName,
    setLogo,
    setColors,
    scope,
    setScope,
    applyColorsToCss,
  } = useBrandStore();
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Local state for form
  const [brandName, setBrandName] = useState(name);
  const [selectedColors, setSelectedColors] = useState(colors);
  const [homePageOnly, setHomePageOnly] = useState(scope === 'homeOnly');

  // Check if user is a super admin
  const superAdmin = isSuperAdmin(user?.role);

  // Load brand settings from database
  useEffect(() => {
    const loadBrandSettings = async () => {
      if (!user?.id) return;

      try {
        const settings = await fetchBrandSettings(user.id);

        if (settings) {
          // Update local state
          setBrandName(settings.name || '');
          setName(settings.name || '');

          if (settings.logo) {
            setLogo(settings.logo);
          }

          const brandColors = {
            primary: settings.primary_color,
            secondary: settings.secondary_color,
            accent: settings.accent_color,
          };

          setSelectedColors(brandColors);
          setColors(brandColors);

          // Set scope
          const currentScope = settings.scope || 'global';
          setHomePageOnly(currentScope === 'homeOnly');
          setScope(currentScope);
        }
      } catch (error) {
        console.error('Error loading brand settings:', error);
      }
    };

    loadBrandSettings();
  }, [user?.id, setColors, setLogo, setName, setScope]);

  // Apply the colors from store when component mounts to ensure consistency
  useEffect(() => {
    applyColorsToCss();
  }, [applyColorsToCss]);

  if (!superAdmin) {
    return (
      <Card className='mt-6'>
        <CardHeader>
          <CardTitle>{t('superAdminBrandSettings.title')}</CardTitle>
          <CardDescription>
            {t('superAdminBrandSettings.description')}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const handleLogoUpload = (imageDataUrl: string | null) => {
    setLogo(imageDataUrl || '');
  };

  const handleColorChange = (
    type: 'primary' | 'secondary' | 'accent',
    value: string
  ) => {
    // Update local state first for a responsive UI
    setSelectedColors({ ...selectedColors, [type]: value });

    // Apply color change immediately for real-time preview
    setColors({ [type]: value });
  };

  const handleScopeChange = (checked: boolean) => {
    setHomePageOnly(checked);
    setScope(checked ? 'homeOnly' : 'global');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !superAdmin) return;

    setIsLoading(true);

    try {
      const brandData: BrandSettingsForm = {
        name: brandName,
        logo: logo,
        primary_color: selectedColors.primary,
        secondary_color: selectedColors.secondary,
        accent_color: selectedColors.accent,
        scope: homePageOnly ? 'homeOnly' : 'global',
      };

      await updateBrandSettings(user.id, brandData);

      // Update global state
      setName(brandName);
      setScope(homePageOnly ? 'homeOnly' : 'global');

      toast({
        title: t('superAdminBrandSettings.settingsSaved'),
        description: t('superAdminBrandSettings.settingsSavedDescription'),
      });
    } catch (error) {
      console.error('Error saving brand settings:', error);

      toast({
        title: t('superAdminBrandSettings.errorSavingSettings'),
        description: t(
          'superAdminBrandSettings.errorSavingSettingsDescription'
        ),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className='grid gap-6'>
        <Card>
          <CardHeader>
            <CardTitle>{t('superAdminBrandSettings.identityTitle')}</CardTitle>
            <CardDescription>
              {t('superAdminBrandSettings.identityDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-6'>
            <div className='space-y-2'>
              <Label htmlFor='brandName'>
                {t('superAdminBrandSettings.brandNameLabel')}
              </Label>
              <Input
                id='brandName'
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className='space-y-2'>
              <Label>{t('superAdminBrandSettings.brandLogoLabel')}</Label>
              <ImageUploader
                onImageUploaded={handleLogoUpload}
                currentImage={logo}
              />
              {logo && (
                <div className='mt-2 p-2 border rounded-md bg-muted/30'>
                  <p className='text-xs text-muted-foreground mb-1'>
                    {t('superAdminBrandSettings.currentLogo')}
                  </p>
                  <img src={logo} alt='Current logo' className='h-10' />
                </div>
              )}
            </div>

            <div className='flex items-center space-x-2'>
              <Switch
                id='scope-switch'
                checked={homePageOnly}
                onCheckedChange={handleScopeChange}
              />
              <Label htmlFor='scope-switch'>
                {t('superAdminBrandSettings.applyColorsToHomePageOnly')}
              </Label>
            </div>

            <div className='space-y-4'>
              <Label>{t('superAdminBrandSettings.brandColorsLabel')}</Label>

              <div className='grid gap-4 sm:grid-cols-3'>
                <div className='space-y-2'>
                  <div className='flex items-center gap-2'>
                    <div
                      className='w-4 h-4 rounded-full'
                      style={{ backgroundColor: selectedColors.primary }}
                    />
                    <span className='text-sm font-medium'>
                      {t('superAdminBrandSettings.primaryColor')}
                    </span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <input
                      type='color'
                      value={selectedColors.primary}
                      onChange={(e) =>
                        handleColorChange('primary', e.target.value)
                      }
                      className='w-full h-10 rounded-md cursor-pointer'
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className='space-y-2'>
                  <div className='flex items-center gap-2'>
                    <div
                      className='w-4 h-4 rounded-full'
                      style={{ backgroundColor: selectedColors.secondary }}
                    />
                    <span className='text-sm font-medium'>
                      {t('superAdminBrandSettings.secondaryColor')}
                    </span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <input
                      type='color'
                      value={selectedColors.secondary}
                      onChange={(e) =>
                        handleColorChange('secondary', e.target.value)
                      }
                      className='w-full h-10 rounded-md cursor-pointer'
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className='space-y-2'>
                  <div className='flex items-center gap-2'>
                    <div
                      className='w-4 h-4 rounded-full'
                      style={{ backgroundColor: selectedColors.accent }}
                    />
                    <span className='text-sm font-medium'>
                      {t('superAdminBrandSettings.accentColor')}
                    </span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <input
                      type='color'
                      value={selectedColors.accent}
                      onChange={(e) =>
                        handleColorChange('accent', e.target.value)
                      }
                      className='w-full h-10 rounded-md cursor-pointer'
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className='mt-4 p-4 border rounded-md bg-muted/30'>
                <h4 className='text-sm font-medium mb-2 flex items-center gap-2'>
                  <Palette className='w-4 h-4' />{' '}
                  {t('superAdminBrandSettings.colorPreview')}
                </h4>
                <div className='flex flex-wrap gap-2'>
                  <Button variant='default'>
                    {t('superAdminBrandSettings.primaryButton')}
                  </Button>
                  <Button variant='secondary'>
                    {t('superAdminBrandSettings.secondaryButton')}
                  </Button>
                  <Button
                    variant='outline'
                    className='border-accent text-accent hover:bg-accent/10'>
                    {t('superAdminBrandSettings.accentButton')}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className='flex justify-end'>
          <Button
            type='submit'
            className='w-full sm:w-auto'
            disabled={isLoading}>
            {isLoading
              ? t('superAdminBrandSettings.saving')
              : t('superAdminBrandSettings.saveSettings')}
          </Button>
        </div>
      </div>
    </form>
  );
}
